<template>
  <NavBar pageName="Settings"></NavBar>

  <v-container style="margin-top: 185px;">
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="d-flex align-center mb-4">
      <v-btn icon variant="text" size="small" @click="$router.push('/settings/msisdn')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h2>MSISDN Inventory</h2>
    </div>

    <!-- Filters -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="3">
        <v-select
          v-model="filterReseller"
          :items="resellerOptions"
          item-title="text"
          item-value="value"
          label="Reseller"
          density="compact"
          clearable
          variant="outlined"
          hide-details
          @update:modelValue="loadData"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="searchTerm"
          label="Search MSISDN"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          @keyup.enter="loadData"
        />
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
          <v-icon class="mr-2">mdi-refresh</v-icon> Refresh
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon> Add MSISDN(s)
        </v-btn>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card>
      <v-card-text>
        <v-data-table-server
          :headers="tableHeaders"
          :items="items"
          :items-length="total"
          :loading="loading"
          :items-per-page="perPage"
          :page="page"
          @update:page="p => { page = p; loadData(); }"
          @update:items-per-page="pp => { perPage = pp; page = 1; loadData(); }"
          item-value="id"
          class="elevation-1"
        >
          <template #[`item.msisdn`]="{ item }">
            <span class="font-weight-medium">{{ item.msisdn }}</span>
          </template>
          <template #[`item.mno_status`]="{ item }">
            <v-chip size="small" variant="tonal">{{ item.mno_status }}</v-chip>
          </template>
          <template #[`item.msisdn_status`]="{ item }">
            <v-chip size="small" variant="tonal" color="info">{{ item.msisdn_status }}</v-chip>
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ item.created_at ? new Date(item.created_at).toLocaleDateString() : '' }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon variant="text" size="small" @click="goToMsisdnsList(item)" color="primary" title="Info">
              <v-icon size="small">mdi-eye</v-icon>
            </v-btn>
            <v-btn icon variant="text" size="small" @click="confirmDelete(item)" color="error">
              <v-icon size="small">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog v-model="showAddDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>Add MSISDN(s)</v-card-title>
        <v-card-text>
          <v-btn-toggle v-model="addMode" mandatory color="primary" class="mb-4">
            <v-btn value="single">Single</v-btn>
            <v-btn value="range">Range</v-btn>
          </v-btn-toggle>

          <template v-if="addMode === 'single'">
            <v-text-field v-model="addForm.msisdn" label="MSISDN" type="number" variant="outlined" density="compact" class="mb-2" />
          </template>
          <template v-else>
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="addForm.msisdn_from" label="MSISDN From" type="number" variant="outlined" density="compact" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="addForm.msisdn_to" label="MSISDN To" type="number" variant="outlined" density="compact" />
              </v-col>
            </v-row>
            <v-alert v-if="rangeCount > 0" type="info" variant="tonal" density="compact" class="mb-2">
              Will create {{ rangeCount }} MSISDN(s)
            </v-alert>
          </template>

          <v-select v-model="addForm.reseller_id" :items="resellerOptions.filter(r => r.value)" item-title="text" item-value="value" label="Reseller" variant="outlined" density="compact" class="mb-2" />
          <v-select v-model="addForm.mvno_id" :items="mvnoOptions" item-title="text" item-value="value" label="MVNO" variant="outlined" density="compact" class="mb-2" />
          <v-select v-model="addForm.msisdn_class_id" :items="msisdnClassOptions" item-title="text" item-value="value" label="MSISDN Class" variant="outlined" density="compact" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showAddDialog = false">Cancel</v-btn>
          <v-btn color="success" variant="flat" :loading="saving" @click="handleAdd">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Deactivate</v-card-title>
        <v-card-text>
          Mark MSISDN <strong>{{ deleteTarget?.msisdn }}</strong> as not available?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="handleDelete">Deactivate</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" location="bottom right">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import { getMsisdnsInventory, addMsisdnsInventory, deleteMsisdnInventory } from "@/composables/useInventoryAPI";
import store from "@/store";

export default defineComponent({
  name: 'MsisdnInventorySettings',
  components: { NavBar },
  data() {
    return {
      loading: false,
      saving: false,
      items: [],
      total: 0,
      page: 1,
      perPage: 50,
      filterReseller: null,
      searchTerm: '',
      showAddDialog: false,
      addMode: 'single',
      addForm: { msisdn: '', msisdn_from: '', msisdn_to: '', reseller_id: null, mvno_id: null, msisdn_class_id: null },
      showDeleteDialog: false,
      deleteTarget: null,
      snackbar: { show: false, message: '', color: 'success' },
      resellerOptions: [
        { text: 'All', value: null },
        { text: '1 - Benefito', value: 1 },
        { text: '2 - NextM2M', value: 2 },
        { text: '3 - Valmed', value: 3 },
        { text: '4 - AdPos', value: 4 },
      ],
      mvnoOptions: [
        { text: 'Veridian', value: 1 },
        { text: 'Benefito', value: 2 },
        { text: 'Vodafone Romania', value: 3 },
        { text: 'Orange Romania', value: 4 },
        { text: 'Digi Romania', value: 5 },
      ],
      msisdnClassOptions: [
        { text: 'Numere obisnuite', value: 99 },
        { text: 'name', value: 1 },
        { text: 'xxx yyy', value: 12 },
        { text: '000', value: 11 },
      ],
      tableHeaders: [
        { title: 'ID', key: 'id', width: '80px' },
        { title: 'MSISDN', key: 'msisdn' },
        { title: 'Reseller', key: 'reseller_id', width: '100px' },
        { title: 'MVNO', key: 'mvno', width: '130px' },
        { title: 'MSISDN Class', key: 'msisdn_class', width: '140px' },
        { title: 'MNO Status', key: 'mno_status', width: '120px' },
        { title: 'MSISDN Status', key: 'msisdn_status', width: '130px' },
        { title: 'Created', key: 'created_at', width: '110px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '80px' },
      ],
    };
  },
  computed: {
    rangeCount() {
      if (this.addMode !== 'range') return 0;
      const count = parseInt(this.addForm.msisdn_to) - parseInt(this.addForm.msisdn_from) + 1;
      return count > 0 ? count : 0;
    }
  },
  methods: {
    async loadData() {
      // Don't load without a search term or reseller filter
      if (!this.filterReseller && (!this.searchTerm || this.searchTerm.length < 3)) {
        this.items = [];
        this.total = 0;
        return;
      }
      this.loading = true;
      const token = store.getters.getToken;
      const params = { page: this.page, per_page: this.perPage };
      if (this.filterReseller) params.reseller_id = this.filterReseller;
      if (this.searchTerm && this.searchTerm.length >= 3) params.search = this.searchTerm;
      const res = await getMsisdnsInventory(token, params);
      if (!res.error) {
        this.items = res.items || [];
        this.total = res.total || 0;
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    openAddDialog() {
      this.addForm = { msisdn: '', msisdn_from: '', msisdn_to: '', reseller_id: null, mvno_id: null, msisdn_class_id: null };
      this.addMode = 'single';
      this.showAddDialog = true;
    },
    async handleAdd() {
      this.saving = true;
      const token = store.getters.getToken;
      const data = {
        mode: this.addMode,
        reseller_id: this.addForm.reseller_id,
        mvno_id: this.addForm.mvno_id,
        msisdn_class_id: this.addForm.msisdn_class_id,
      };
      if (this.addMode === 'single') {
        data.msisdn = this.addForm.msisdn;
      } else {
        data.msisdn_from = this.addForm.msisdn_from;
        data.msisdn_to = this.addForm.msisdn_to;
      }
      const res = await addMsisdnsInventory(token, data);
      if (!res.error) {
        this.showSnackbar(res.message, 'success');
        this.showAddDialog = false;
        await this.loadData();
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.saving = false;
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.showDeleteDialog = true;
    },
    async handleDelete() {
      this.showDeleteDialog = false;
      this.loading = true;
      const token = store.getters.getToken;
      const res = await deleteMsisdnInventory(token, this.deleteTarget.id);
      if (!res.error) {
        this.showSnackbar(res.message, 'success');
        await this.loadData();
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    goToMsisdnsList(item) {
      this.$router.push({ name: 'msisdns', query: { search: item.msisdn } });
    },
    showSnackbar(message, color) {
      this.snackbar = { show: true, message, color };
    }
  },
  mounted() {
    this.loadData();
  }
});
</script>
